import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Plus, 
  ArrowRight,
  Building2,
  Mail,
  MailX,
  Phone,
  MapPin,
  Sparkles,
  GripVertical,
  ExternalLink,
  Calendar,
  Tag,
  Archive,
  Filter,
  SortAsc,
  SortDesc,
  X,
  ChevronDown,
  RotateCcw,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Loader2,
  Truck,
  MessageSquare,
  Download,
  Eye,
  Reply,
  CalendarCheck,
  Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'discovered', label: 'Discovered', color: 'bg-gray-100 border-gray-200' },
  { key: 'reviewed', label: 'Reviewed', color: 'bg-blue-50 border-blue-200' },
  { key: 'outreach_sent', label: 'Outreach Sent', color: 'bg-purple-50 border-purple-200' },
  { key: 'replied', label: 'Replied', color: 'bg-amber-50 border-amber-200' },
  { key: 'declined', label: 'Declined', color: 'bg-red-50 border-red-200' },
  { key: 'meeting_scheduled', label: 'Meeting', color: 'bg-cyan-50 border-cyan-200' },
  { key: 'active_client', label: 'Active Client', color: 'bg-green-50 border-green-200' },
  { key: 'archived', label: 'Archived', color: 'bg-stone-100 border-stone-300' },
];

const SORT_OPTIONS = [
  { key: 'score_desc', label: 'Score (High to Low)', icon: SortDesc },
  { key: 'score_asc', label: 'Score (Low to High)', icon: SortAsc },
  { key: 'date_desc', label: 'Newest First', icon: SortDesc },
  { key: 'date_asc', label: 'Oldest First', icon: SortAsc },
  { key: 'name_asc', label: 'Name (A-Z)', icon: SortAsc },
  { key: 'name_desc', label: 'Name (Z-A)', icon: SortDesc },
  { key: 'distance_asc', label: 'Distance (Nearest)', icon: SortAsc },
  { key: 'distance_desc', label: 'Distance (Farthest)', icon: SortDesc },
];

const Pipeline = () => {
  const [leads, setLeads] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [newLead, setNewLead] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    category: '',
    website: '',
    segment_id: '',
    notes: ''
  });

  // Rescore state
  const [rescoreOpen, setRescoreOpen] = useState(false);
  const [rescoreFilters, setRescoreFilters] = useState({
    categories: [],
    cities: [],
    stages: [],
    score_min: 0,
    score_max: 100,
    date_from: '',
    date_to: ''
  });
  const [rescoring, setRescoring] = useState(false);
  const [rescoreJob, setRescoreJob] = useState(null);

  // Distance refresh state
  const [distRefreshing, setDistRefreshing] = useState(false);
  const [distJob, setDistJob] = useState(null);

  // Filter state — persisted in sessionStorage across navigation
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('pipeline_filters');
      return saved ? JSON.parse(saved) : {
        categories: [], cities: [], stages: [],
        scoreRange: [0, 100], distanceRange: [0, 100],
        dateFrom: '', dateTo: '', searchTerm: '', emailStatus: 'all'
      };
    } catch { return { categories: [], cities: [], stages: [], scoreRange: [0, 100], distanceRange: [0, 100], dateFrom: '', dateTo: '', searchTerm: '', emailStatus: 'all' }; }
  });
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem('pipeline_sortBy') || 'date_desc');
  const [showFilters, setShowFilters] = useState(() => {
    try { return sessionStorage.getItem('pipeline_showFilters') === 'true'; } catch { return false; }
  });

  // Persist filter/sort changes to sessionStorage
  useEffect(() => { sessionStorage.setItem('pipeline_filters', JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem('pipeline_sortBy', sortBy); }, [sortBy]);
  useEffect(() => { sessionStorage.setItem('pipeline_showFilters', String(showFilters)); }, [showFilters]);

  // Get unique values for filter dropdowns.
  // Category dropdown pulls from BOTH lead.category (raw SerpAPI type) and
  // lead.segment_name (user-editable bucket). Without this union, a lead whose
  // segment was changed in the edit form still matches the old raw category in
  // the filter and confuses the user.
  const uniqueCategories = useMemo(() => {
    const seen = new Map(); // lowercased -> original casing
    for (const lead of leads) {
      for (const value of [lead.category, lead.segment_name]) {
        if (!value) continue;
        const key = value.trim().toLowerCase();
        if (!seen.has(key)) seen.set(key, value);
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const uniqueCities = useMemo(() => {
    const cities = leads.map(l => l.city).filter(Boolean);
    return [...new Set(cities)].sort();
  }, [leads]);

  const uniqueDeliveryZones = useMemo(() => {
    const zones = leads.map(l => l.delivery_zone).filter(Boolean);
    return [...new Set(zones)].sort();
  }, [leads]);

  const maxDistance = useMemo(() => {
    const distances = leads.map(l => l.distance_miles).filter(d => d != null);
    return distances.length > 0 ? Math.ceil(Math.max(...distances)) : 100;
  }, [leads]);

  // Apply filters and sorting
  // We split into two stages so emailCounts can reflect leads matching all
  // OTHER active filters (excluding the email-status filter itself). Otherwise
  // the dropdown shows misleading global counts when, say, a city filter is on.
  const leadsExcludingEmailFilter = useMemo(() => {
    let result = [...leads];

    // Search term filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(lead =>
        lead.business_name?.toLowerCase().includes(term) ||
        lead.contact_name?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term)
      );
    }

    // Category filter — match against either category or segment_name (case-insensitive)
    // so changes made through the Segment dropdown in the edit form also affect
    // the filter, not just the raw category captured at discovery time.
    if (filters.categories.length > 0) {
      const selected = new Set(filters.categories.map(c => c.trim().toLowerCase()));
      result = result.filter(lead => {
        const cat = lead.category?.trim().toLowerCase();
        const seg = lead.segment_name?.trim().toLowerCase();
        return (cat && selected.has(cat)) || (seg && selected.has(seg));
      });
    }

    // City filter
    if (filters.cities.length > 0) {
      result = result.filter(lead => filters.cities.includes(lead.city));
    }

    // Stage filter
    if (filters.stages.length > 0) {
      result = result.filter(lead => filters.stages.includes(lead.stage));
    }

    // Score range filter
    result = result.filter(lead => {
      const score = lead.claude_score || 0;
      return score >= filters.scoreRange[0] && score <= filters.scoreRange[1];
    });

    // Distance range filter
    if (filters.distanceRange[0] > 0 || filters.distanceRange[1] < maxDistance) {
      result = result.filter(lead => {
        if (lead.distance_miles == null) return filters.distanceRange[0] === 0; // Include if no distance and min is 0
        return lead.distance_miles >= filters.distanceRange[0] && lead.distance_miles <= filters.distanceRange[1];
      });
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      result = result.filter(lead => new Date(lead.created_at) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(lead => new Date(lead.created_at) <= toDate);
    }

    return result;
  }, [leads, filters.searchTerm, filters.categories, filters.cities, filters.stages, filters.scoreRange, filters.distanceRange, filters.dateFrom, filters.dateTo, maxDistance]);

  // Email counts reflect leads matching all OTHER active filters, so the
  // dropdown labels stay accurate when you also filter by city/category/etc.
  const emailCounts = useMemo(() => {
    const withEmail = leadsExcludingEmailFilter.filter(l => l.email).length;
    return { withEmail, withoutEmail: leadsExcludingEmailFilter.length - withEmail };
  }, [leadsExcludingEmailFilter]);

  // Final filtered list: apply email-status filter on top, then sort.
  const filteredLeads = useMemo(() => {
    let result = leadsExcludingEmailFilter;

    if (filters.emailStatus === 'has_email') {
      result = result.filter(lead => lead.email);
    } else if (filters.emailStatus === 'no_email') {
      result = result.filter(lead => !lead.email);
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'score_desc':
          return (b.claude_score || 0) - (a.claude_score || 0);
        case 'score_asc':
          return (a.claude_score || 0) - (b.claude_score || 0);
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return (a.business_name || '').localeCompare(b.business_name || '');
        case 'name_desc':
          return (b.business_name || '').localeCompare(a.business_name || '');
        case 'distance_asc':
          return (a.distance_miles ?? 999) - (b.distance_miles ?? 999);
        case 'distance_desc':
          return (b.distance_miles ?? 0) - (a.distance_miles ?? 0);
        default:
          return 0;
      }
    });

    return result;
  }, [leadsExcludingEmailFilter, filters.emailStatus, sortBy]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.cities.length > 0) count++;
    if (filters.stages.length > 0) count++;
    if (filters.scoreRange[0] > 0 || filters.scoreRange[1] < 100) count++;
    if (filters.distanceRange[0] > 0 || filters.distanceRange[1] < maxDistance) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.searchTerm) count++;
    if (filters.emailStatus && filters.emailStatus !== 'all') count++;
    return count;
  }, [filters, maxDistance]);

  const resetFilters = () => {
    setFilters({
      categories: [],
      cities: [],
      stages: [],
      scoreRange: [0, 100],
      distanceRange: [0, maxDistance],
      dateFrom: '',
      dateTo: '',
      searchTerm: '',
      emailStatus: 'all'
    });
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/leads/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const toggleFilterValue = (filterKey, value) => {
    setFilters(prev => {
      const current = prev[filterKey];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterKey]: newValues };
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, segmentsRes] = await Promise.all([
        axios.get(`${API}/leads`, { withCredentials: true }),
        axios.get(`${API}/segments`, { withCredentials: true })
      ]);
      setLeads(leadsRes.data);
      setSegments(segmentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async () => {
    if (!newLead.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }

    try {
      await axios.post(`${API}/leads`, newLead, { withCredentials: true });
      toast.success('Lead created successfully');
      setNewLeadOpen(false);
      setNewLead({
        business_name: '',
        contact_name: '',
        email: '',
        phone: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        category: '',
        website: '',
        segment_id: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}/stage?stage=${newStage}`, {}, { withCredentials: true });
      toast.success('Stage updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleArchiveLead = async (leadId) => {
    try {
      await axios.put(`${API}/leads/${leadId}/archive`, {}, { withCredentials: true });
      toast.success('Lead archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive lead');
    }
  };

  const [resendingScheduleLeadId, setResendingScheduleLeadId] = useState(null);
  const handleResendScheduleLink = async (leadId) => {
    setResendingScheduleLeadId(leadId);
    try {
      const res = await axios.post(
        `${API}/leads/${leadId}/resend-schedule-link`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Scheduling link sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send scheduling link');
    } finally {
      setResendingScheduleLeadId(null);
    }
  };

  const handleStartRescore = async () => {
    setRescoring(true);
    try {
      const response = await axios.post(`${API}/leads/rescore`, rescoreFilters, { withCredentials: true });
      toast.success(`Rescoring ${response.data.lead_count} leads...`);
      setRescoreJob(response.data);
      
      // Poll for completion
      const pollRescore = async () => {
        try {
          const jobResponse = await axios.get(`${API}/rescore-jobs/${response.data.job_id}`, { withCredentials: true });
          setRescoreJob(jobResponse.data);
          
          if (jobResponse.data.status === 'completed') {
            toast.success(jobResponse.data.progress_message);
            setRescoreOpen(false);
            setRescoring(false);
            setRescoreJob(null);
            fetchData(); // Refresh leads
          } else if (jobResponse.data.status === 'failed') {
            toast.error('Rescore failed');
            setRescoring(false);
          } else {
            setTimeout(pollRescore, 2000);
          }
        } catch (err) {
          // Polling transient failure — retry silently
          setTimeout(pollRescore, 3000);
        }
      };
      
      setTimeout(pollRescore, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rescore');
      setRescoring(false);
    }
  };

  const toggleRescoreFilter = (filterKey, value) => {
    setRescoreFilters(prev => {
      const current = prev[filterKey];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterKey]: newValues };
    });
  };

  // Distance stats
  const leadsWithDistance = useMemo(() => leads.filter(l => l.distance_miles != null).length, [leads]);
  const leadsMissingDistance = useMemo(() => leads.filter(l => l.distance_miles == null).length, [leads]);

  const handleRefreshDistances = async () => {
    setDistRefreshing(true);
    setDistJob(null);
    try {
      const response = await axios.post(`${API}/leads/calculate-distances`, {}, { withCredentials: true });
      toast.success(`Distance refresh started for ${response.data.leads_count} leads`);
      setDistJob({ status: 'running', processed: 0, total_leads: response.data.leads_count, geocoded: 0 });

      const pollDist = async () => {
        try {
          const res = await axios.get(`${API}/leads/distance-job/${response.data.job_id}`, { withCredentials: true });
          setDistJob(res.data);
          if (res.data.status === 'completed') {
            setDistRefreshing(false);
            toast.success(`Distances refreshed! ${res.data.geocoded} leads geocoded out of ${res.data.processed} processed.`);
            fetchData();
          } else if (res.data.status === 'failed') {
            setDistRefreshing(false);
            toast.error('Distance refresh failed');
          } else {
            setTimeout(pollDist, 3000);
          }
        } catch {
          // Polling transient failure — retry silently
          setTimeout(pollDist, 5000);
        }
      };
      setTimeout(pollDist, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start distance refresh');
      setDistRefreshing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getLeadsByStage = (stage) => filteredLeads.filter(lead => lead.stage === stage);

  const getScoreClass = (score) => {
    if (!score) return '';
    if (score >= 70) return 'score-high';
    if (score >= 40) return 'score-medium';
    return 'score-low';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Lead Pipeline</h1>
          <p className="text-muted-foreground mt-1">
            {filteredLeads.length} of {leads.length} leads
            {activeFilterCount > 0 && <span className="text-forest"> (filtered)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export CSV Button */}
          <Button
            onClick={handleExportCSV}
            variant="outline"
            data-testid="export-csv-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          {/* Rescore Button */}
          <Dialog open={rescoreOpen} onOpenChange={setRescoreOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="rescore-btn">
                <RefreshCw className="mr-2 h-4 w-4" />
                Rescore Leads
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Rescore Leads</DialogTitle>
                <DialogDescription>
                  Re-analyze selected leads with AI to update their scores based on current information.
                </DialogDescription>
              </DialogHeader>
              
              {rescoring && rescoreJob ? (
                <div className="py-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-forest" />
                  <p className="font-medium">{rescoreJob.progress_message}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {rescoreJob.processed || 0} / {rescoreJob.total_leads || 0} processed
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      Select which leads to rescore, or leave all filters empty to rescore all leads.
                    </p>
                    
                    {/* Category Filter */}
                    <div>
                      <Label className="text-sm">Categories</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {uniqueCategories.slice(0, 10).map(cat => (
                          <Badge
                            key={cat}
                            variant={rescoreFilters.categories.includes(cat) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleRescoreFilter('categories', cat)}
                          >
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* City Filter */}
                    <div>
                      <Label className="text-sm">Cities</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {uniqueCities.slice(0, 10).map(city => (
                          <Badge
                            key={city}
                            variant={rescoreFilters.cities.includes(city) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleRescoreFilter('cities', city)}
                          >
                            {city}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* Score Range */}
                    <div>
                      <Label className="text-sm">
                        Score Range: {rescoreFilters.score_min} - {rescoreFilters.score_max}
                      </Label>
                      <div className="px-2 pt-3">
                        <Slider
                          value={[rescoreFilters.score_min, rescoreFilters.score_max]}
                          onValueChange={([min, max]) => setRescoreFilters(prev => ({ ...prev, score_min: min, score_max: max }))}
                          min={0}
                          max={100}
                          step={5}
                        />
                      </div>
                    </div>
                    
                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">From Date</Label>
                        <Input
                          type="date"
                          value={rescoreFilters.date_from}
                          onChange={(e) => setRescoreFilters(prev => ({ ...prev, date_from: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">To Date</Label>
                        <Input
                          type="date"
                          value={rescoreFilters.date_to}
                          onChange={(e) => setRescoreFilters(prev => ({ ...prev, date_to: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setRescoreOpen(false)}>Cancel</Button>
                    <Button 
                      onClick={handleStartRescore}
                      className="bg-forest hover:bg-forest-dark"
                      disabled={rescoring}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Start Rescore
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
          
          {/* Add Lead Button */}
          <Dialog open={newLeadOpen} onOpenChange={setNewLeadOpen}>
            <DialogTrigger asChild>
              <Button className="bg-wheat hover:bg-wheat-dark text-white" data-testid="add-lead-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Lead</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={newLead.business_name}
                    onChange={(e) => setNewLead({ ...newLead, business_name: e.target.value })}
                    placeholder="e.g., The Coffee House"
                  data-testid="input-business-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={newLead.contact_name}
                    onChange={(e) => setNewLead({ ...newLead, contact_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="segment">Segment</Label>
                  <Select
                    value={newLead.segment_id}
                    onValueChange={(value) => setNewLead({ ...newLead, segment_id: value })}
                  >
                    <SelectTrigger data-testid="select-segment">
                      <SelectValue placeholder="Select segment" />
                    </SelectTrigger>
                    <SelectContent>
                      {segments.map((segment) => (
                        <SelectItem key={segment.segment_id} value={segment.segment_id}>
                          {segment.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={newLead.category}
                    onChange={(e) => setNewLead({ ...newLead, category: e.target.value })}
                    placeholder="e.g., Coffee Shop, Restaurant"
                    data-testid="input-category"
                  />
                </div>
                <div>
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    value={newLead.street_address}
                    onChange={(e) => setNewLead({ ...newLead, street_address: e.target.value })}
                    placeholder="123 Main St"
                    data-testid="input-street-address"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={newLead.city}
                    onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                    placeholder="e.g., Westchester"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={newLead.state}
                    onChange={(e) => setNewLead({ ...newLead, state: e.target.value })}
                    placeholder="e.g., NY"
                    data-testid="input-state"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input
                    id="zip_code"
                    value={newLead.zip_code}
                    onChange={(e) => setNewLead({ ...newLead, zip_code: e.target.value })}
                    placeholder="e.g., 10001"
                    data-testid="input-zip"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={newLead.website}
                  onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                  placeholder="https://example.com"
                  data-testid="input-website"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  placeholder="Any initial notes about this lead..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewLeadOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateLead}
                className="bg-forest hover:bg-forest-dark text-white"
                data-testid="submit-lead-btn"
              >
                Add Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Distance Refresh Banner */}
      {(leadsMissingDistance > 0 || distRefreshing) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="distance-refresh-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                {distRefreshing && distJob ? (
                  <>
                    <p className="font-medium text-sm text-amber-800">Refreshing distances...</p>
                    <p className="text-xs text-amber-600">
                      Processed {distJob.processed || 0} of {distJob.total_leads || '?'} leads
                      {distJob.geocoded > 0 && ` — ${distJob.geocoded} geocoded`}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-sm text-amber-800">
                      {leadsMissingDistance} lead{leadsMissingDistance !== 1 ? 's' : ''} missing distance data
                    </p>
                    <p className="text-xs text-amber-600">
                      {leadsWithDistance} of {leads.length} leads have distances calculated
                    </p>
                  </>
                )}
              </div>
            </div>
            <Button
              onClick={handleRefreshDistances}
              disabled={distRefreshing}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
              data-testid="refresh-distances-btn"
            >
              {distRefreshing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Calculating...</>
              ) : (
                <><MapPin className="mr-2 h-4 w-4" />Refresh Distances</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Filter & Sort Bar */}
      <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-4">
        {/* Search and Quick Actions Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-sm">
            <Input
              placeholder="Search leads..."
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="h-9"
              data-testid="search-leads"
            />
          </div>

          {/* Email Status Filter */}
          <Select
            value={filters.emailStatus || 'all'}
            onValueChange={(value) => setFilters(prev => ({ ...prev, emailStatus: value }))}
          >
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="email-status-filter">
              <Mail className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads ({leads.length})</SelectItem>
              <SelectItem value="has_email">Has Email ({emailCounts.withEmail})</SelectItem>
              <SelectItem value="no_email">No Email ({emailCounts.withoutEmail})</SelectItem>
            </SelectContent>
          </Select>

          {/* Filter Toggle Button */}
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-forest hover:bg-forest-dark" : ""}
            data-testid="toggle-filters"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {/* Sort Dropdown */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] h-9" data-testid="sort-leads">
              <SortDesc className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(option => (
                <SelectItem key={option.key} value={option.key}>
                  <span className="flex items-center gap-2">
                    <option.icon className="h-3 w-3" />
                    {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Reset Filters */}
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
        </div>

        {/* Expanded Filter Options */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-stone-100">
            {/* Category Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Category</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-9 text-sm">
                    {filters.categories.length > 0 
                      ? `${filters.categories.length} selected`
                      : "All categories"}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2 max-h-[300px] overflow-y-auto">
                  {uniqueCategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">No categories found</p>
                  ) : (
                    uniqueCategories.map(cat => (
                      <div
                        key={cat}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-stone-100 ${
                          filters.categories.includes(cat) ? 'bg-forest/10' : ''
                        }`}
                        onClick={() => toggleFilterValue('categories', cat)}
                      >
                        <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                          filters.categories.includes(cat) ? 'bg-forest border-forest' : 'border-stone-300'
                        }`}>
                          {filters.categories.includes(cat) && <span className="text-white text-xs">✓</span>}
                        </div>
                        <span className="text-sm truncate">{cat}</span>
                      </div>
                    ))
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* City Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">City</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-9 text-sm">
                    {filters.cities.length > 0 
                      ? `${filters.cities.length} selected`
                      : "All cities"}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2 max-h-[300px] overflow-y-auto">
                  {uniqueCities.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">No cities found</p>
                  ) : (
                    uniqueCities.map(city => (
                      <div
                        key={city}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-stone-100 ${
                          filters.cities.includes(city) ? 'bg-forest/10' : ''
                        }`}
                        onClick={() => toggleFilterValue('cities', city)}
                      >
                        <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                          filters.cities.includes(city) ? 'bg-forest border-forest' : 'border-stone-300'
                        }`}>
                          {filters.cities.includes(city) && <span className="text-white text-xs">✓</span>}
                        </div>
                        <span className="text-sm truncate">{city}</span>
                      </div>
                    ))
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* Status Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Status</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-9 text-sm">
                    {filters.stages.length > 0 
                      ? `${filters.stages.length} selected`
                      : "All statuses"}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2 max-h-[300px] overflow-y-auto">
                  {STAGES.map(stage => (
                    <div
                      key={stage.key}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-stone-100 ${
                        filters.stages.includes(stage.key) ? 'bg-forest/10' : ''
                      }`}
                      onClick={() => toggleFilterValue('stages', stage.key)}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                        filters.stages.includes(stage.key) ? 'bg-forest border-forest' : 'border-stone-300'
                      }`}>
                        {filters.stages.includes(stage.key) && <span className="text-white text-xs">✓</span>}
                      </div>
                      <span className="text-sm truncate">{stage.label}</span>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
            </div>

            {/* Score Range Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">
                Score Range: {filters.scoreRange[0]} - {filters.scoreRange[1]}
              </Label>
              <div className="px-2 pt-2">
                <Slider
                  value={filters.scoreRange}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, scoreRange: value }))}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Discovery Date</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="h-9 text-sm"
                  placeholder="From"
                />
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="h-9 text-sm"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        )}

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {filters.categories.map(cat => (
              <Badge key={`cat-${cat}`} variant="secondary" className="gap-1">
                <Tag className="h-3 w-3" />
                {cat}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => toggleFilterValue('categories', cat)}
                />
              </Badge>
            ))}
            {filters.cities.map(city => (
              <Badge key={`city-${city}`} variant="secondary" className="gap-1">
                <MapPin className="h-3 w-3" />
                {city}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => toggleFilterValue('cities', city)}
                />
              </Badge>
            ))}
            {filters.stages.map(stageKey => {
              const stage = STAGES.find(s => s.key === stageKey);
              return (
                <Badge key={`stage-${stageKey}`} variant="secondary" className="gap-1">
                  {stage?.label || stageKey}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => toggleFilterValue('stages', stageKey)}
                  />
                </Badge>
              );
            })}
            {(filters.scoreRange[0] > 0 || filters.scoreRange[1] < 100) && (
              <Badge variant="secondary" className="gap-1">
                <Sparkles className="h-3 w-3" />
                Score: {filters.scoreRange[0]}-{filters.scoreRange[1]}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => setFilters(prev => ({ ...prev, scoreRange: [0, 100] }))}
                />
              </Badge>
            )}
            {filters.dateFrom && (
              <Badge variant="secondary" className="gap-1">
                <Calendar className="h-3 w-3" />
                From: {filters.dateFrom}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                />
              </Badge>
            )}
            {filters.dateTo && (
              <Badge variant="secondary" className="gap-1">
                <Calendar className="h-3 w-3" />
                To: {filters.dateTo}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                />
              </Badge>
            )}
            {filters.emailStatus && filters.emailStatus !== 'all' && (
              <Badge variant="secondary" className="gap-1" data-testid="email-filter-tag">
                <Mail className="h-3 w-3" />
                {filters.emailStatus === 'has_email' ? 'Has Email' : 'No Email'}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => setFilters(prev => ({ ...prev, emailStatus: 'all' }))}
                />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Pipeline Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage) => (
            <div 
              key={stage.key}
              className={`w-72 flex-shrink-0 rounded-xl border ${stage.color} p-4`}
              data-testid={`pipeline-column-${stage.key}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-sm">{stage.label}</h3>
                <span className="text-xs text-muted-foreground bg-white px-2 py-1 rounded-full">
                  {getLeadsByStage(stage.key).length}
                </span>
              </div>
              
              <div className="space-y-3">
                {getLeadsByStage(stage.key).map((lead) => (
                  <div
                    key={lead.lead_id}
                    className="pipeline-card bg-white rounded-lg border border-stone-200 p-4 shadow-sm"
                    data-testid={`lead-card-${lead.lead_id}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/leads/${lead.lead_id}`}
                          className="font-medium text-sm hover:text-forest transition-colors line-clamp-2"
                        >
                          {lead.business_name}
                        </Link>
                        {/* No email indicator */}
                        {!lead.email && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-600 border border-red-200 mt-1"
                            title="No email address"
                            data-testid={`no-email-${lead.lead_id}`}
                          >
                            <MailX className="h-2.5 w-2.5" />
                            No email
                          </span>
                        )}
                        {/* Segment */}
                        {lead.segment_name && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-forest/10 text-forest border border-forest/20">
                              {lead.segment_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Lead info - City & Date */}
                    <div className="space-y-1 mb-2 text-xs text-muted-foreground">
                      {(lead.city || lead.full_address) && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">
                            {lead.city}{lead.state ? `, ${lead.state}` : ''}{!lead.city && lead.full_address ? lead.full_address : ''}
                          </span>
                        </div>
                      )}
                      {lead.created_at && (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 flex-shrink-0" />
                          <span>Discovered {formatDate(lead.created_at)}</span>
                        </div>
                      )}
                    </div>

                    {/* Score, Distance, Activity Indicators & Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        {lead.claude_score ? (
                          <div className="flex items-center gap-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getScoreClass(lead.claude_score)}`}>
                              <Sparkles className="inline h-3 w-3 mr-1" />
                              {lead.claude_score}
                            </span>
                            {lead.score_change && lead.score_change !== 0 && (
                              <span className={`flex items-center text-xs font-medium ${lead.score_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {lead.score_change > 0 ? (
                                  <TrendingUp className="h-3 w-3" />
                                ) : (
                                  <TrendingDown className="h-3 w-3" />
                                )}
                                <span className="ml-0.5">{lead.score_change > 0 ? '+' : ''}{lead.score_change}</span>
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not scored</span>
                        )}
                        {lead.distance_miles != null && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            <Truck className="inline h-3 w-3 mr-1" />
                            {lead.distance_miles} mi
                          </span>
                        )}
                        {/* Activity indicators */}
                        {lead.email_opened && (
                          <span className="p-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200" title="Email opened" data-testid={`indicator-opened-${lead.lead_id}`}>
                            <Eye className="h-3 w-3" />
                          </span>
                        )}
                        {(lead.stage === 'replied' || lead.interaction_history?.some(i => i.type === 'reply_received')) && (
                          <span className="p-1 rounded-full bg-green-50 text-green-600 border border-green-200" title="Replied" data-testid={`indicator-replied-${lead.lead_id}`}>
                            <Reply className="h-3 w-3" />
                          </span>
                        )}
                        {lead.stage === 'meeting_scheduled' && (
                          <span className="p-1 rounded-full bg-amber-50 text-amber-600 border border-amber-200" title="Meeting scheduled" data-testid={`indicator-meeting-${lead.lead_id}`}>
                            <CalendarCheck className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {lead.email && stage.key !== 'archived' && (
                          <button
                            onClick={() => handleResendScheduleLink(lead.lead_id)}
                            disabled={resendingScheduleLeadId === lead.lead_id}
                            className="px-1.5 py-1 rounded text-forest hover:bg-forest/10 transition-colors disabled:opacity-50"
                            title="Email a fresh scheduling link to this lead"
                            data-testid={`resend-schedule-${lead.lead_id}`}
                          >
                            {resendingScheduleLeadId === lead.lead_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {stage.key !== 'archived' && (
                          <button
                            onClick={() => handleArchiveLead(lead.lead_id)}
                            className="p-1 text-muted-foreground hover:text-amber-600 transition-colors"
                            title="Archive lead"
                            data-testid={`archive-lead-${lead.lead_id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        <Link 
                          to={`/leads/${lead.lead_id}`}
                          className="p-1 text-forest hover:text-forest-dark"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>

                    {/* Stage selector */}
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <Select
                        value={lead.stage}
                        onValueChange={(value) => handleStageChange(lead.lead_id, value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.map((s) => (
                            <SelectItem key={s.key} value={s.key} className="text-xs">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}

                {getLeadsByStage(stage.key).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No leads in this stage
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pipeline;
